// The MOTION paper — a constitutional amendment, as one member sees it.
//
// Sibling of ./PositionPaper.tsx, and deliberately not a variant of it: a motion
// has an instrument to read, a majority it is measured against, and three
// answers; an election has candidates, places and No Confidence. The two papers
// share a VOCABULARY (`.vote-row`, `.ballot-*`, <Yardstick>, <CastStatus>,
// <ReturnBlankAction>) rather than one component with a mode flag, which is what
// keeps a change to one from silently re-dressing the other.
//
// Everything the position paper's header sets out about native
// <fieldset>/<legend>/<input type="radio">, about marking being free, silent and
// local, about 48px targets, about there being no tally, turnout or countdown on
// a voting paper, and about a blank paper being a separate act rather than a
// fourth row, holds here unchanged and is not restated.
//
// What is particular to a MOTION:
//
//  • THE INSTRUMENT IS RENDERED IN FULL, ALWAYS. No truncation, no "show more",
//    no scroll box, no line clamp, and no scroll-gate on the submit button. A
//    member who cannot read the whole motion cannot consent to it — and a
//    control that unlocked on a scroll event would be a dark pattern for a
//    sighted reader and meaningless for a screen-reader one, who never fires it.
//    A blank line starts a new paragraph and NOTHING else is done to the text;
//    the argument, including why there is no `dangerouslySetInnerHTML` anywhere
//    near it, is at `instrumentParagraphs` in ../paper.
//  • THE YARDSTICK IS GLUED TO THE ROWS. It sits below the instrument and
//    directly above the fieldset it describes, so a long motion pushes the two
//    down TOGETHER and the sentence saying what majority this vote is measured
//    against is never more than one panel above the first row. It is also named
//    on the fieldset's `aria-describedby`, so a keyboard or screen-reader user
//    meets it on entering the radio group rather than tabbing past it.
//  • ABSTAIN IS AN EQUAL PEER OF AYE AND NO — same element, same class, same
//    size and weight, no colour, no icon, no separator, and no branch in the map
//    below through which one could be added. Abstaining is a lawful, recorded
//    choice, and subordinating it would be this platform editorialising about
//    how a member should vote. `MOTION_ROWS` (../paper) builds all three in one
//    list for exactly that reason.
//  • THERE IS NO No Confidence ROW. SO 15.7 is an ELECTIONS provision; there is
//    no such answer to a motion, and a fourth row would invent a lawful choice.
//  • WHAT AN ABSTENTION DOES TO THE COUNT IS SAID BENEATH THE ROWS, and it is
//    keyed on the majority base because the answer genuinely changes with the
//    denominator (`ABSTENTION_NOTE`, ../thresholds). Under one of the three
//    bases the registrar has ratified that the paper says nothing, so nothing is
//    rendered — no words are invented to fill the space.
//  • AN ATTRIBUTABLE BALLOT SAYS SO BEFORE THE FIRST ROW IS MARKED, from the
//    ballot row's own `secrecy`. **This is the first surface on which that
//    branch can actually fire**: an election is always written `secret`
//    (SO 15.9, and a CHECK), so only a motion can lawfully be published with the
//    members' names against it. The block is the position paper's, word for
//    word — one sentence, said in one place on both papers.
//
// It is a RENDERER and it decides nothing. Whether this member may hold this
// paper at all is settled server-side on the request that issued it, and
// re-settled on the request that records it.

import { useId, type FormEvent, type JSX } from 'react';
import { CastStatus, type CastAct } from '../CastStatus';
import { MARK_INSTRUCTION, MOTION_ROWS, paperHouseLabel, type MarkableMotion } from '../paper';
import { ABSTENTION_NOTE } from '../thresholds';
import { Yardstick } from '../Yardstick';
import type { CastPaperView } from '../api/client';
import { ReturnBlankAction } from './BlankConfirm';

interface MotionPaperProps {
  paper: CastPaperView;
  /** The one motion on this paper, narrowed by ./CastPage — a paper carrying a
   *  motion this screen cannot read, or more than one item to mark, never
   *  reaches this component. */
  motion: MarkableMotion;
  /** Who the member is, in their own words — the address they signed in with.
   *  The roll holds no names, so this is the honest answer to "you are voting
   *  as", and it comes from the session rather than from the paper. */
  voterLabel: string;
  /** The marked row's key, or null. Owned by the parent because the submit is.
   *  There is no `rows` prop to go with it, and that is not an omission: a
   *  motion's rows are the same three rows on every paper, so `MOTION_ROWS` is
   *  one module constant that this component and the parent's submit both read
   *  — see its note in ../paper. */
  marked: string | null;
  onMark: (key: string) => void;
  onSubmit: () => void;
  /** Opens the blank-paper warning. It SENDS NOTHING — see ./BlankConfirm. It is
   *  offered unconditionally, never only on an unmarked paper: a control that
   *  appeared only when nothing was marked would tell anyone watching the screen
   *  that this member has marked nothing. */
  onReturnBlank: () => void;
  submitting: boolean;
  act: CastAct;
}

export function MotionPaper({
  paper,
  motion,
  voterLabel,
  marked,
  onMark,
  onSubmit,
  onReturnBlank,
  submitting,
  act,
}: MotionPaperProps): JSX.Element {
  const groupName = useId();
  // The yardstick's SENTENCE, named so the fieldset can describe itself with it.
  const yardstickTextId = useId();
  const houseLabel = paperHouseLabel(paper.paper.voterHouse);
  const abstentionNote = ABSTENTION_NOTE[motion.base];
  const attributed = motion.mover !== null || motion.seconder !== null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <form className="ballot" onSubmit={handleSubmit}>
      {/* The BALLOT this paper belongs to, then that it is taking papers. Shape
          and word together, never colour alone. A paper in hand is the proof
          that the poll is open — the route refuses to issue one otherwise — so
          there is no clock here and no countdown; a poll that closes while a
          member is still reading a long motion is met at the submit, in words,
          at the moment it matters. */}
      <p className="ballot-eyebrow">
        <span>{paper.ballot.title}</span>
        <span className="ballot-state">
          <span className="ballot-dot" aria-hidden="true" />
          <span className="ballot-sr">Voting </span>
          Open
        </span>
      </p>

      <h1 className="ballot-title">{motion.title}</h1>

      {/* Who you are voting AS. The House is not decoration on a motion either:
          under a `summoned-per-house` majority the two Houses are counted
          separately and a motion carries only if BOTH carry it (§15.2), so which
          tally this paper lands in is part of what is being asked. It is the
          House STAMPED ON THE PAPER, not the one on the roll, and where the
          stamp is a word this build cannot name the address stands alone rather
          than beside a blank. */}
      <p className="ballot-elector-lead">You are voting as</p>
      <p className="ballot-elector">
        {voterLabel}
        {houseLabel === null ? '' : ` · ${houseLabel}`}
      </p>

      {/* NOT A SECRET BALLOT — said before a single row is marked, and only ever
          on the ballot row's own `secrecy`. On an attributable ballot the paper
          NAMES its elector in the database
          (`paper_elector_contact_id_iff_attributable`) and the vote is published
          with the result, which is a thing a member must know while they can
          still decide what to do about it. The secret case says nothing: that is
          what this whole surface is built for, and a badge reading "secret"
          would be chrome on every paper. */}
      {paper.ballot.secrecy === 'attributable' && (
        <p className="alert">
          <strong>This ballot is not secret.</strong> Your vote is recorded against your name and is
          published with the result.
        </p>
      )}

      <hr className="ballot-rule" />

      {/* THE INSTRUMENT, VERBATIM AND ENTIRE — the words Synod is being asked to
          agree. One <p> per paragraph of the stored text and nothing else done
          to it; the whole rule, and the reason there is no markup rendering
          here, is at `instrumentParagraphs` in ../paper. Real paragraphs rather
          than one string with line breaks in it, because a screen reader
          navigates by paragraph and a member re-reading clause 3 of an amendment
          needs to be able to get back to it. */}
      <div className="ballot-instrument">
        {motion.paragraphs.map((paragraph, index) => (
          // The text of a motion is fixed for the life of this paper and its
          // paragraphs have no identity of their own, so the index is the
          // honest key: this list is never reordered, inserted into or filtered.
          <p className="ballot-instrument-text" key={index}>
            {paragraph}
          </p>
        ))}
        {/* Moved and seconded — a description list, because that is what a pair
            of labelled names is. A name the record does not hold draws no row
            at all rather than a term beside a blank; see MarkableMotion. */}
        {attributed && (
          <dl className="ballot-attrib">
            {motion.mover !== null && (
              <div className="ballot-attrib-row">
                <dt>Moved</dt>
                <dd>{motion.mover}</dd>
              </div>
            )}
            {motion.seconder !== null && (
              <div className="ballot-attrib-row">
                <dt>Second</dt>
                <dd>{motion.seconder}</dd>
              </div>
            )}
          </dl>
        )}
      </div>

      <hr className="ballot-rule" />

      {/* WHAT THIS MOTION NEEDS — above the rows and below the instrument, and
          the position between them is load-bearing rather than aesthetic. Below
          the rows it would be off-screen behind the pinned action bar at exactly
          the scroll position where the choice is marked (Miranda's ruling,
          2026-08-15: "the order is only safe on the viewport where nobody needs
          the help"). Above the instrument it would be separated from the rows by
          the whole text of the amendment. Glued here, a long motion pushes the
          yardstick and the fieldset down together.

          `level={2}` because the motion title above is this page's h1, and the
          same component and the same three sentences appear on the results
          sheet — one table (../thresholds), so the paper and the report cannot
          come to describe the same clause differently. */}
      <Yardstick base={motion.base} level={2} textId={yardstickTextId} />

      {/* `aria-describedby` names the yardstick's SENTENCE, so entering the
          radio group announces what majority this vote is measured against. A
          heading above a group is only met by a reader who travels through it;
          arrowing or tabbing in goes straight past. */}
      <fieldset className="ballot-votes" aria-describedby={yardstickTextId}>
        <legend className="ballot-legend">Your vote</legend>
        <p className="ballot-instruction">{MARK_INSTRUCTION}</p>
        {MOTION_ROWS.map((row) => (
          // ONE MAP, ONE SHAPE. Aye, No and Abstain are the same element with
          // the same class — there is no branch here, so there is nowhere for a
          // colour, an icon or a smaller row to be added to one of them.
          <label className="vote-row" key={row.key}>
            <input
              className="radio vote-radio"
              type="radio"
              name={groupName}
              value={row.key}
              checked={marked === row.key}
              // Locked only while a submission is in flight. Marking is
              // otherwise free: it submits nothing, navigates nowhere and moves
              // no focus, and the mark stays local and changeable until Submit.
              disabled={submitting}
              onChange={() => {
                onMark(row.key);
              }}
            />
            <span className="vote-label">{row.label}</span>
          </label>
        ))}
        {/* WHAT AN ABSTENTION DOES, under THIS majority — and nothing at all
            under the base for which the registrar has ratified that the paper
            says nothing. The table is keyed on the base beside the yardstick's
            own sentences so the two cannot drift; see ../thresholds. */}
        {abstentionNote !== undefined && <p className="ballot-note">{abstentionNote}</p>}
      </fieldset>

      {/* THE OTHER LAWFUL WAY OFF THIS PAPER (registrar ruling 2026-08-27).
          Below the vote, behind the paper's own rule, and OUTSIDE the action bar
          — it is a second act, not a second submit. It sends nothing; it opens
          the warning. The whole argument is at the head of ./BlankConfirm.tsx,
          and it is kind-agnostic: `blank` is a word on a motion paper too, and
          the wire for a returned paper is `{ marks: [] }` either way. */}
      <hr className="ballot-rule" />
      <ReturnBlankAction onAsk={onReturnBlank} disabled={submitting} />

      {/* ABOVE the action bar: the bar is pinned over the bottom of the scroll
          port on a phone, so a message after it is a message nobody reads. It is
          this paper's only live region, and it is incapable of carrying the mark
          — ../CastStatus is a closed union of act tokens, with no string prop. */}
      <CastStatus act={act} />

      <div className="action-bar ballot-actions">
        <button
          type="submit"
          className="btn btn-primary btn-block ballot-submit"
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Submit my vote'}
        </button>
      </div>
    </form>
  );
}
